import { useCallback, useEffect, useState } from "react";
import type { ProjectInviteLink } from "@baton/contracts";
import type { DashboardApi } from "./api.js";
import { CopyField } from "./components/command.js";
import { Modal } from "./components/modal.js";
import { inviteUrl } from "./invite.js";
import { PeopleList, type ProjectPeopleState } from "./people-list.js";
import { InvitationEmailForm } from "./invitation-email.js";

const EXPIRIES: Array<{ label: string; hours: number | null }> = [
  { label: "24 hours", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
];

/** Admin-only: invite people Baton already knows in one click, and mint invite links (shown once). */
export function InviteDialog(props: {
  open: boolean;
  onClose(): void;
  api: DashboardApi;
  projectId: string;
  projectName: string;
  isOwner: boolean;
  /** The cockpit's shared people list — the same rows the rail shows. */
  people: ProjectPeopleState;
}) {
  const [role, setRole] = useState<"member" | "admin">("member");
  const [hours, setHours] = useState<number | null>(24 * 7);
  const [maxUses, setMaxUses] = useState<string>("");
  const [links, setLinks] = useState<ProjectInviteLink[]>([]);
  const [created, setCreated] = useState<{ url: string; expiresAt: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [personRole, setPersonRole] = useState<"member" | "admin">("member");
  const [email, setEmail] = useState("");

  const load = useCallback(async () => {
    try { setLinks(await props.api.listInviteLinks(props.projectId)); } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load invite links");
    }
  }, [props.api, props.projectId]);

  const reloadPeople = props.people.reload;
  useEffect(() => {
    if (!props.open) { setCreated(null); setError(null); return; }
    void load();
    void reloadPeople();
  }, [props.open, load, reloadPeople]);

  useEffect(() => { if (!props.people.canInvite) { setPersonRole("member"); setRole("member"); } }, [props.people.canInvite]);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setWorking(true); setError(null);
    try {
      const link = await props.api.createInviteLink(props.projectId, {
        role,
        expiresAt: hours === null ? null : new Date(Date.now() + hours * 60 * 60 * 1_000).toISOString(),
        maxUses: maxUses ? Number(maxUses) : null,
      });
      setCreated({ url: link.url ?? inviteUrl(window.location.origin, link.token), expiresAt: link.expiresAt });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create the invite link");
    } finally {
      setWorking(false);
    }
  }

  async function revoke(linkId: string) {
    setWorking(true); setError(null);
    try { await props.api.revokeInviteLink(props.projectId, linkId); await load(); } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not revoke the invite link");
    } finally { setWorking(false); }
  }

  const active = links.filter((link) => !link.revokedAt);
  return <Modal open={props.open} onClose={props.onClose} title={`Invite people to ${props.projectName}`}>
    <div className="invite-dialog" data-testid="invite-dialog">
      <p className="muted">Invite collaborators from any repository in this project. They sign in with GitHub and join as a member or admin. Members can send handovers; admins can also manage repositories and people.</p>

      <section className="invite-people" data-testid="invite-people">
        <h4>People Baton already knows</h4>
        <p className="muted compact">Click Invite to create an in-app invitation. To notify them by email, use the section below. GitHub-only collaborators also get a single-use link you can copy and share.</p>
        {props.people.error && <p className="error">{props.people.error}</p>}
        <PeopleList
          rows={props.people.rows}
          variant="full"
          canInvite={props.people.canInvite}
          role={personRole}
          onRoleChange={setPersonRole}
          allowAdminRole={props.people.canInvite}
          busyUserId={props.people.busyUserId}
          outcomes={props.people.outcomes}
          onInvite={(person) => void props.people.invite(person, personRole)}
          emptyLabel="Link a repository and add collaborators on GitHub to invite them here."
        />
      </section>

      {props.open && props.people.canInvite && <InvitationEmailForm key={props.projectId} api={props.api} projectId={props.projectId} rows={props.people.rows} />}

      <h4>Invite link</h4>
      <form className="invite-form" onSubmit={create}>
        <label>Role
          <select value={role} onChange={(event) => setRole(event.target.value as "member" | "admin")}>
            <option value="member">member</option>
            {props.people.canInvite && <option value="admin">admin</option>}
          </select>
        </label>
        <label>Expires
          <select value={hours ?? ""} onChange={(event) => setHours(event.target.value ? Number(event.target.value) : null)}>
            {EXPIRIES.map((expiry) => <option key={expiry.label} value={expiry.hours ?? ""}>{expiry.label}</option>)}
          </select>
        </label>
        <label>Max uses
          <input type="number" min={1} max={1000} value={maxUses} onChange={(event) => setMaxUses(event.target.value)} placeholder="unlimited" />
        </label>
        <button type="submit" className="primary" disabled={working}>Create link</button>
      </form>
      {created && <aside className="bootstrap-notice invite-created">
        <strong>Share this link with a repository collaborator.</strong>
        <CopyField label="Invite link" value={created.url} />
        <div className="email-invite">
          <label>Collaborator email (optional)<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="teammate@example.com" /></label>
          <a href={`mailto:${encodeURIComponent(email.trim())}?subject=${encodeURIComponent(`Join ${props.projectName} on Baton`)}&body=${encodeURIComponent(`You are invited to ${props.projectName} on Baton. Sign in with the GitHub account that collaborates on one of the project's repositories:\n\n${created.url}`)}`}>Invite by email</a>
          <small className="muted">Opens a draft in your email app. You choose when to send it.</small>
        </div>
        {created.expiresAt && <small className="muted">Expires {new Date(created.expiresAt).toLocaleString()}</small>}
      </aside>}
      {error && <p className="error">{error}</p>}
      <h4>Active links</h4>
      {active.length === 0 && <p className="empty compact">No active invite links.</p>}
      <ul className="invite-list">
        {active.map((link) => <li key={link.id}>
          <span><strong>{link.role}</strong> · {link.useCount}{link.maxUses ? `/${link.maxUses}` : ""} used · {link.expiresAt ? `expires ${new Date(link.expiresAt).toLocaleDateString()}` : "no expiry"}</span>
          <button type="button" className="danger" disabled={working} onClick={() => void revoke(link.id)}>Revoke</button>
        </li>)}
      </ul>
    </div>
  </Modal>;
}
